/* ============================================================
   cards-center: /pages/mijn-wishlist
   ----------------------------------------------------------
   Volledige redesign — premium card-styling matched met cart,
   plus features voor TCG-collectors:

   1. Sorteren (alfabetisch / prijs / recent / voorraad)
   2. Bulk "Alles op voorraad in winkelwagen"
   3. Totale waarde + op-voorraad waarde in summary
   4. Toegevoegd-op datum per item ("3 dagen geleden")
   5. Vendor-filter chips (Pokémon / Yu-Gi-Oh / Lorcana / ...)
   6. Live search binnen wishlist
   7. Voorraad-urgency badge ("Laatste 3!" oranje pulse)
   8. Undo-verwijderen via gedeelde cc-cart-toast
   9. Voorraad-progress bar in summary

   Bron: localStorage 'shopify_wishlist' (gedeeld met heart-toggle
   in assets/wishlist-custom.js). Velden per item:
     { id, handle, title, image, price, added_at, available }

   Verse data wordt bij elke pageload + handmatige refresh
   opgehaald uit /products/{handle}.js (current price, vendor,
   inventory_quantity per variant). Daarna update ook localStorage
   zodat de heart-toggle elders dezelfde verse prijs/availability
   gebruikt.
   ============================================================ */
(function () {
    'use strict';

    const STORAGE_KEY = 'shopify_wishlist';
    const AUTO_REMOVE_KEY = 'cc_mwl_auto_remove';
    const PRODUCT_CACHE_TTL = 60 * 1000; // 1 min sessionStorage cache
    const UNDO_TIMEOUT_MS = 5000;
    const SEARCH_DEBOUNCE_MS = 180;
    // Image-widths voor srcset — Shopify CDN serveert deze on-the-fly.
    // Cards zijn ~280px breed op desktop (5 kol op 1410px), 2x retina = 560,
    // 3x = 840. We bieden 400/600/900/1200 zodat browsers altijd een goede
    // resolutie kunnen kiezen voor hun scherm + viewport.
    const IMAGE_WIDTHS = [400, 600, 900, 1200];

    /* -------------------------------------------------------- */
    /* Helpers                                                  */
    /* -------------------------------------------------------- */

    // Taal, valuta en adressen van de bezoeker (2026-10-28): teksten komen uit de taalbestanden van het thema
    // (cc.wishlist.*, via <script id="cc-mwl-i18n"> in templates/page.wishlist-custom.liquid). Ontbreekt dat blok,
    // dan blijven de Nederlandse standaardteksten hieronder staan. Placeholders: {n}, {title}, {done}, {total}.
    const I18N = (() => {
        try { return JSON.parse(document.getElementById('cc-mwl-i18n').textContent) || {}; } catch (e) { return {}; }
    })();
    const t = (key, fallback, vars) => String(I18N[key] || fallback)
        .replace(/\{(\w+)\}/g, (m, k) => (vars && k in vars ? vars[k] : m));
    const ROOT = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/?$/, '/');
    const LANG = document.documentElement.lang || 'nl';
    const productUrl = (handle) => `${ROOT}products/${encodeURIComponent(handle)}`;

    const moneyFmt = new Intl.NumberFormat(LANG, {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR',
        minimumFractionDigits: 2,
    });
    const formatMoney = (cents) => moneyFmt.format((cents || 0) / 100);

    function escapeHtml(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
            ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
        );
    }

    function relativeAdded(iso) {
        if (!iso) return '';
        const ts = new Date(iso).getTime();
        if (isNaN(ts)) return '';
        const diff = Math.max(0, Date.now() - ts);
        const days = Math.floor(diff / 86400000);
        if (days < 1) return t('today', 'Vandaag');
        if (days === 1) return t('day_ago', '1 dag geleden');
        if (days < 7) return t('days_ago', '{n} dagen geleden', { n: days });
        if (days < 30) return t('weeks_ago', '{n}w geleden', { n: Math.floor(days / 7) });
        if (days < 365) return t('months_ago', '{n}mnd geleden', { n: Math.floor(days / 30) });
        return t('years_ago', '{n}j geleden', { n: Math.floor(days / 365) });
    }

    /* Parse item.price uit localStorage -> cents.
       item.price kan zijn:
         - Number (cents): bv 1599 (van eerdere mijn-wishlist refresh-flow,
           of van heart-toggle die direct cents opslaat). Direct returnen.
         - Formatted string: bv "€15,99", "€15.99", "15,99 EUR". Parse de
           eerste getal-sequence en × 100 voor cents.
         - null/undefined/lege string → 0.
       Alleen gebruikt als fallback wanneer this.products géén verse data
       voor dit handle heeft. */
    function parsePriceToCents(s) {
        if (s == null || s === '') return 0;
        if (typeof s === 'number') return s; // aanname: cents
        const m = String(s).replace(/\s/g, '').match(/(-?\d+(?:[.,]\d+)?)/);
        if (!m) return 0;
        return Math.round(parseFloat(m[1].replace(',', '.')) * 100);
    }

    function debounce(fn, ms) {
        let t;
        return function () {
            const ctx = this, args = arguments;
            clearTimeout(t);
            t = setTimeout(() => fn.apply(ctx, args), ms);
        };
    }

    function showToast(opts) {
        if (typeof window.ccShowToast === 'function') {
            window.ccShowToast(opts);
        } else {
            // Fallback als cart-popup-preference.js (nog) niet geladen is —
            // simpele alert-vrije console-log fallback. In praktijk altijd
            // aanwezig want het script wordt globaal geladen via scripts-tag.
            console.log('[wishlist toast]', opts.message);
        }
    }

    /* -------------------------------------------------------- */
    /* WishlistPage                                             */
    /* -------------------------------------------------------- */

    class WishlistPage {
        constructor(root) {
            this.root = root;

            // DOM refs (gebonden in template)
            this.skeletonEl = root.querySelector('.cc-mwl-skeleton-grid');
            this.contentEl = root.querySelector('.cc-mwl-content');
            this.summaryEl = root.querySelector('.cc-mwl-summary');
            this.controlsEl = root.querySelector('.cc-mwl-controls');
            this.chipsEl = root.querySelector('.cc-mwl-chips');
            this.sectionsEl = root.querySelector('.cc-mwl-sections');
            this.emptyEl = root.querySelector('.cc-mwl-empty');
            this.loadingEl = root.querySelector('.cc-mwl-loading-indicator');
            this.loadingFillEl = root.querySelector('.cc-mwl-loading-bar-fill');
            this.modalEl = root.querySelector('.cc-mwl-modal');

            // State
            this.items = this.readItems();          // localStorage source-of-truth
            this.products = new Map();              // handle -> {available, priceCents, vendor, lowStockQty, variantId, productId, qtyMin, qtyMax, qtyStep, title}
            this.cartCache = null;                  // /cart.js snapshot voor max-check
            this.ui = {
                sort: 'recent',
                search: '',
                vendor: 'all',
                autoRemove: localStorage.getItem(AUTO_REMOVE_KEY) === '1',
            };

            // Op deze pagina willen we GEEN cart-drawer openen na add-to-cart
            // (drawer-content wordt niet sectie-replaced bij onze custom calls).
            // De flag wordt gelezen door cart-popup-preference.js patched .open()
            // die hier alleen nog de toast toont. Daarnaast: capture-phase intercept
            // op cart-trigger clicks zodat het cart-icoon in de header direct naar
            // /cart navigeert (native browser nav) ipv de drawer te openen.
            window.ccDisableCartDrawer = true;
            this.installCartTriggerIntercept();

            this.bindEvents();
            this.fetchCart();                       // achtergrond cart-cache vullen

            // Eerste render: als geen items, direct empty state.
            // Anders: skeleton tonen tot verse data binnen is.
            if (this.items.length === 0) {
                if (this.skeletonEl) this.skeletonEl.style.display = 'none';
                this.renderEmpty(t('empty_title', 'Je wishlist is leeg'), t('empty_text', 'Klik op het hartje bij producten om ze hier te verzamelen. Zo zie je in één oogopslag wanneer een kaart of product weer op voorraad is.'), true);
            } else {
                this.showSkeleton();
                this.refreshAll();
            }
        }

        /* ------------------ localStorage I/O ------------------ */

        readItems() {
            try {
                const raw = localStorage.getItem(STORAGE_KEY);
                const arr = raw ? JSON.parse(raw) : [];
                return Array.isArray(arr) ? arr : [];
            } catch (e) {
                console.error('Wishlist parse error:', e);
                return [];
            }
        }

        writeItems() {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
                // Keep header counter in sync (heart-toggle leest hetzelfde key)
                if (window.shopifyWishlist) window.shopifyWishlist.updateCounter();
                else this.updateHeaderCounter();
            } catch (e) {
                console.error('Wishlist save error:', e);
            }
        }

        updateHeaderCounter() {
            const counter = document.getElementById('wishlist-count');
            if (counter) counter.textContent = String(this.items.length);
        }

        /* ------------------ Refresh / fetch ------------------ */

        async refreshAll(force = false) {
            if (this.items.length === 0) {
                this.hideSkeleton();
                this.renderEmpty(t('empty_title', 'Je wishlist is leeg'), t('empty_text', 'Klik op het hartje bij producten om ze hier te verzamelen. Zo zie je in één oogopslag wanneer een kaart of product weer op voorraad is.'), true);
                return;
            }
            if (force) this.clearProductCache();

            this.setLoadingProgress(0, this.items.length);
            this.showLoadingBar();

            const total = this.items.length;
            let done = 0;

            await Promise.all(this.items.map(async (item) => {
                try {
                    const data = await this.fetchProduct(item.handle);
                    this.products.set(item.handle, data);
                    // Sync alleen `available` terug naar localStorage zodat
                    // de wishlist-counter en cross-tab sync de juiste status
                    // zien. Item.price BEWUST onaangeroerd: behoud de
                    // originele formatted string van de heart-toggle als
                    // fallback voor wanneer this.products géén verse data
                    // heeft. Display-prijs komt altijd uit data.priceCents.
                    // (Eerder werd item.price overschreven met cents-number;
                    // bij een falende fetch raakte de string corrupt naar 0.)
                    item.available = data.available;
                    if (!item.title && data.title) item.title = data.title;
                } catch (e) {
                    // Fallback: gebruik bestaande velden, mark unavailable
                    this.products.set(item.handle, {
                        available: false,
                        priceCents: parsePriceToCents(item.price),
                        vendor: '',
                        lowStockQty: null,
                        variantId: null,
                        productId: null,
                        qtyMin: 1,
                        qtyMax: 0,
                        qtyStep: 1,
                        title: item.title,
                    });
                    item.available = false;
                }
                done++;
                this.setLoadingProgress(done, total);
            }));

            this.writeItems();
            this.hideSkeleton();
            this.hideLoadingBar();
            this.render();
        }

        async fetchProduct(handle) {
            const cacheKey = `cc_mwl_p_${ROOT}${handle}`; // per taal/markt, want titel en prijs verschillen
            try {
                const cached = sessionStorage.getItem(cacheKey);
                if (cached) {
                    const obj = JSON.parse(cached);
                    if (obj.t && Date.now() - obj.t < PRODUCT_CACHE_TTL) return obj.d;
                }
            } catch (e) { /* ignore */ }

            // Probeer eerst de custom alt-template (levert basis-info +
            // qty-metafields + low-stock + first-available variant in één call).
            // Zie templates/product.cc-wishlist-data.liquid. Als die template
            // niet is geüpload via Shopify admin krijgen we HTML terug ipv JSON
            // → JSON.parse faalt → val terug op standaard /products/{handle}.js
            // (geen qty-metafields, maar wel werkende prijs/availability/vendor).
            const data = await this.fetchProductViaView(handle).catch(() => null)
                      || await this.fetchProductViaJs(handle);

            try {
                sessionStorage.setItem(cacheKey, JSON.stringify({ t: Date.now(), d: data }));
            } catch (e) { /* quota — negeer */ }

            return data;
        }

        async fetchProductViaView(handle) {
            const res = await fetch(`${productUrl(handle)}?view=cc-wishlist-data`, {
                headers: { 'Accept': 'application/json' },
            });
            if (!res.ok) throw new Error('view http error');
            const text = await res.text();
            const raw = JSON.parse(text); // throws bij HTML response
            // Sanity check — view-template moet `id` en `price` velden hebben.
            // Als één van beide ontbreekt is het waarschijnlijk geen geldige
            // view-output (bv. een fallback HTML page die per ongeluk JSON-like is).
            if (typeof raw.id !== 'number' || typeof raw.price !== 'number') {
                throw new Error('view returned invalid data');
            }
            return {
                available: !!raw.available,
                priceCents: raw.price,
                vendor: raw.vendor || '',
                lowStockQty: raw.low_stock_qty || null,
                variantId: raw.variant_id || null,
                productId: raw.id,
                qtyMin: parseInt(raw.qty_min, 10) || 1,
                qtyMax: parseInt(raw.qty_max, 10) || 0,
                qtyStep: parseInt(raw.qty_step, 10) || 1,
                title: raw.title,
            };
        }

        async fetchProductViaJs(handle) {
            // Standaard Shopify product JSON endpoint — werkt altijd, ongeacht
            // theme-templates. Geen toegang tot metafields dus qty-min/max/step
            // krijgen defaults (1/0/1).
            const res = await fetch(`${productUrl(handle)}.js`, {
                headers: { 'Accept': 'application/json' },
            });
            if (!res.ok) throw new Error('product not found');
            const product = await res.json();

            // Eerst-beschikbare variant voor add-to-cart.
            const availableVariants = (product.variants || []).filter(v => v.available);
            const firstAvail = availableVariants[0] || (product.variants || [])[0] || null;

            // Low-stock: alleen voor variants met inventory_management.
            let lowStockQty = null;
            const tracked = (product.variants || []).filter(v => v.inventory_management);
            if (tracked.length > 0) {
                const total = tracked
                    .filter(v => v.available)
                    .reduce((sum, v) => sum + Math.max(0, v.inventory_quantity || 0), 0);
                if (total > 0 && total <= 3) lowStockQty = total;
            }

            return {
                available: !!product.available,
                priceCents: typeof product.price === 'number' ? product.price : parsePriceToCents(product.price),
                vendor: product.vendor || '',
                lowStockQty,
                variantId: firstAvail ? firstAvail.id : null,
                productId: product.id || null,
                qtyMin: 1,        // metafields niet beschikbaar via .js
                qtyMax: 0,
                qtyStep: 1,
                title: product.title,
            };
        }

        /* ------------------ Cart cache + max-check ------------------ */

        async fetchCart() {
            try {
                const r = await fetch('/cart.js', {
                    headers: { 'Accept': 'application/json' },
                    credentials: 'same-origin',
                });
                if (r.ok) this.cartCache = await r.json();
            } catch (e) { /* keep stale */ }
        }

        getProductQtyInCart(productId) {
            if (!this.cartCache || !this.cartCache.items || !productId) return 0;
            const pid = parseInt(productId, 10);
            if (!isFinite(pid)) return 0;
            let sum = 0;
            for (const it of this.cartCache.items) {
                if (it && it.product_id === pid) sum += it.quantity;
            }
            return sum;
        }

        // Returnt {ok: true} of {ok: false, reason, max}. Reason matched het
        // formaat dat cart-popup-preference.onCartMaxReached verwacht.
        checkCartMax(productData, addQty) {
            const max = productData.qtyMax;
            if (!max || max <= 0) return { ok: true };
            if (!productData.productId) return { ok: true };
            const inCart = this.getProductQtyInCart(productData.productId);
            if (inCart + addQty > max) {
                return { ok: false, reason: 'meta-max', max, metaMax: max, inCart };
            }
            return { ok: true };
        }

        dispatchMaxReached(detail) {
            document.dispatchEvent(new CustomEvent('cc:cart-max-reached', { detail }));
        }

        /* ------------------ Cart-drawer bypass ------------------ */

        installCartTriggerIntercept() {
            // Capture-phase op WINDOW (vóór document/body listeners van theme)
            // zodat theme's drawer-open click-handler nooit wordt aangeroepen.
            // Native browser-navigation gebeurt wel (geen preventDefault),
            // dus de <a href="/cart"> link werkt gewoon. Onze eigen toast-link
            // in #cc-cart-toast moet niet onderschept worden — die mag de
            // normale flow volgen want die zit niet in een theme-trigger.
            window.addEventListener('click', (e) => {
                const trigger = e.target.closest('a[href$="/cart"], a[href$="/cart/"]'); // ook /de-de/cart e.d.
                if (!trigger) return;
                if (trigger.closest('#cc-cart-toast')) return;
                e.stopImmediatePropagation();
            }, true);
        }

        clearProductCache() {
            try {
                Object.keys(sessionStorage).forEach((k) => {
                    if (k.startsWith('cc_mwl_p_')) sessionStorage.removeItem(k);
                });
            } catch (e) { /* ignore */ }
        }

        /* ------------------ Loading / skeleton ------------------ */

        showSkeleton() {
            if (!this.skeletonEl) return;
            // Render N skeleton cards = aantal items (max 10) zodat layout stabiel is
            const n = Math.min(10, Math.max(this.items.length, 4));
            const html = Array(n).fill(0).map(() => `
                <div class="cc-mwl-skeleton">
                    <div class="cc-mwl-skeleton-img"></div>
                    <div class="cc-mwl-skeleton-body">
                        <div class="cc-mwl-skeleton-line short"></div>
                        <div class="cc-mwl-skeleton-line"></div>
                        <div class="cc-mwl-skeleton-line tall"></div>
                    </div>
                </div>
            `).join('');
            this.skeletonEl.innerHTML = html;
            this.skeletonEl.style.display = 'grid';
            if (this.contentEl) this.contentEl.style.display = 'none';
        }

        hideSkeleton() {
            if (this.skeletonEl) this.skeletonEl.style.display = 'none';
            if (this.contentEl) this.contentEl.style.display = '';
        }

        showLoadingBar() {
            if (this.loadingEl) this.loadingEl.style.display = 'flex';
        }

        hideLoadingBar() {
            if (this.loadingEl) this.loadingEl.style.display = 'none';
        }

        setLoadingProgress(done, total) {
            if (!this.loadingFillEl) return;
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            this.loadingFillEl.style.width = pct + '%';
            const labelEl = this.root.querySelector('.cc-mwl-loading-label');
            if (labelEl) labelEl.textContent = t('checking_progress', 'Voorraad checken… {done}/{total}', { done, total });
        }

        /* ------------------ Filtering / sorting ------------------ */

        getFiltered() {
            const term = this.ui.search.trim().toLowerCase();
            const vendor = this.ui.vendor;
            return this.items.filter((it) => {
                const data = this.products.get(it.handle);
                const itemVendor = (data && data.vendor) || '';
                if (vendor !== 'all' && itemVendor !== vendor) return false;
                if (term) {
                    const hay = `${it.title || ''} ${itemVendor}`.toLowerCase();
                    if (!hay.includes(term)) return false;
                }
                return true;
            });
        }

        getSorted(arr) {
            const sorted = arr.slice();
            switch (this.ui.sort) {
                case 'alpha':
                    sorted.sort((a, b) => this.titleOf(a).localeCompare(this.titleOf(b), LANG, { sensitivity: 'base' }));
                    break;
                case 'price-asc':
                    sorted.sort((a, b) => this.priceOf(a) - this.priceOf(b));
                    break;
                case 'price-desc':
                    sorted.sort((a, b) => this.priceOf(b) - this.priceOf(a));
                    break;
                case 'recent':
                    sorted.sort((a, b) => {
                        const ta = a.added_at ? new Date(a.added_at).getTime() : 0;
                        const tb = b.added_at ? new Date(b.added_at).getTime() : 0;
                        return tb - ta;
                    });
                    break;
                case 'stock':
                    // available eerst, daarna alphabetisch
                    sorted.sort((a, b) => {
                        const av = (this.products.get(a.handle)?.available) ? 0 : 1;
                        const bv = (this.products.get(b.handle)?.available) ? 0 : 1;
                        if (av !== bv) return av - bv;
                        return this.titleOf(a).localeCompare(this.titleOf(b), LANG, { sensitivity: 'base' });
                    });
                    break;
            }
            return sorted;
        }

        priceOf(item) {
            const data = this.products.get(item.handle);
            return data ? data.priceCents : parsePriceToCents(item.price);
        }

        /* ------------------ Render ------------------ */

        render() {
            const filtered = this.getSorted(this.getFiltered());
            const inStock = filtered.filter(i => this.products.get(i.handle)?.available);
            const outStock = filtered.filter(i => !this.products.get(i.handle)?.available);

            this.renderHeaderCounts();
            this.renderSummary(inStock, outStock);
            this.renderChips();
            this.renderSections(inStock, outStock);
            this.toggleEmptyState(filtered.length === 0);
        }

        renderHeaderCounts() {
            const titleCount = this.root.querySelector('.cc-mwl-title-count');
            if (titleCount) {
                titleCount.textContent = `(${this.items.length})`;
            }
        }

        renderSummary(inStock, outStock) {
            if (!this.summaryEl) return;
            const inCount = inStock.length;
            const outCount = outStock.length;
            const totalItems = this.items.length;

            // Totale waarde op basis van ALLE items (niet gefilterd) zodat
            // de cijfers altijd kloppen met "23 producten" claim.
            let totalCents = 0;
            let inStockCents = 0;
            this.items.forEach((it) => {
                const c = this.priceOf(it);
                totalCents += c;
                if (this.products.get(it.handle)?.available) inStockCents += c;
            });

            const pct = totalItems > 0 ? Math.round((inCount / totalItems) * 100) : 0;

            const subEl = this.root.querySelector('.cc-mwl-subtitle');
            if (subEl) {
                let parts = [`<strong>${totalItems}</strong> ${escapeHtml(totalItems === 1 ? t('product_one', 'product') : t('product_other', 'producten'))}`];
                parts.push(`<strong>${formatMoney(totalCents)}</strong> ${escapeHtml(t('summary_total', 'totaal'))}`);
                if (inStockCents !== totalCents && inCount < totalItems) {
                    parts.push(`<strong>${formatMoney(inStockCents)}</strong> ${escapeHtml(t('summary_in_stock', 'op voorraad'))}`);
                }
                subEl.innerHTML = parts.join(' · ');
            }

            const valueEl = this.summaryEl.querySelector('.cc-mwl-stat-row');
            if (valueEl) {
                valueEl.innerHTML = `
                    <span>${escapeHtml(t('in_stock', 'Op voorraad'))}</span>
                    <span class="cc-mwl-stat-value">${inCount}<span style="font-size:13px;font-weight:500;color:#6b7280;"> / ${totalItems}</span></span>
                    ${outCount > 0 ? `<span style="color:#9ca3af;">·</span><span>${escapeHtml(t('sold_out_count', '{n} uitverkocht', { n: outCount }))}</span>` : ''}
                `;
            }

            const fillEl = this.summaryEl.querySelector('.cc-mwl-progress-fill');
            const labelEl = this.summaryEl.querySelector('.cc-mwl-progress-label');
            if (fillEl) fillEl.style.width = pct + '%';
            if (labelEl) labelEl.textContent = `${pct}%`;

            // Bulk-knop staat in de header (root), niet in summary
            const bulkBtn = this.root.querySelector('.cc-mwl-bulk-add');
            if (bulkBtn) {
                bulkBtn.disabled = inCount === 0;
                const lbl = bulkBtn.querySelector('.cc-mwl-bulk-add-label');
                if (lbl) {
                    lbl.textContent = inCount > 0
                        ? t('bulk_add_count', 'Alles op voorraad ({n})', { n: inCount })
                        : t('bulk_none', 'Geen items op voorraad');
                }
            }
        }

        renderChips() {
            if (!this.chipsEl) return;
            // Tel per vendor
            const vendorCounts = new Map();
            this.items.forEach((it) => {
                const v = (this.products.get(it.handle)?.vendor || '').trim();
                if (!v) return;
                vendorCounts.set(v, (vendorCounts.get(v) || 0) + 1);
            });
            const vendors = Array.from(vendorCounts.entries())
                .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

            // Niet tonen als er maar 1 (of nul) vendor is — geen filter nodig
            if (vendors.length <= 1) {
                this.chipsEl.style.display = 'none';
                this.chipsEl.innerHTML = '';
                return;
            }

            this.chipsEl.style.display = '';
            const allActive = this.ui.vendor === 'all' ? 'is-active' : '';
            let html = `
                <button type="button" class="cc-mwl-chip ${allActive}" data-vendor="all">
                    ${escapeHtml(t('chip_all', 'Alle'))}
                    <span class="cc-mwl-chip-count">${this.items.length}</span>
                </button>
            `;
            vendors.forEach(([v, count]) => {
                const active = this.ui.vendor === v ? 'is-active' : '';
                html += `
                    <button type="button" class="cc-mwl-chip ${active}" data-vendor="${escapeHtml(v)}">
                        ${escapeHtml(v)}
                        <span class="cc-mwl-chip-count">${count}</span>
                    </button>
                `;
            });
            this.chipsEl.innerHTML = html;
        }

        renderSections(inStock, outStock) {
            if (!this.sectionsEl) return;
            let html = '';
            if (inStock.length > 0) {
                html += this.renderSection(t('in_stock', 'Op voorraad'), inStock, 'in');
            }
            if (outStock.length > 0) {
                html += this.renderSection(t('sold_out', 'Uitverkocht'), outStock, 'out');
            }
            this.sectionsEl.innerHTML = html;
        }

        // Titel in de taal van de bezoeker: verse productdata (opgehaald via de taal-URL), anders wat bij het hartje werd opgeslagen
        titleOf(item) {
            return (this.products.get(item.handle)?.title || item.title || '').toString();
        }

        renderSection(title, items, kind) {
            const cards = items.map((it) => this.renderCard(it, kind === 'in')).join('');
            return `
                <section class="cc-mwl-section cc-mwl-section--${kind}">
                    <header class="cc-mwl-section-header">
                        <h2 class="cc-mwl-section-title">
                            ${escapeHtml(title)}
                            <span class="cc-mwl-section-title-count">${items.length}</span>
                        </h2>
                    </header>
                    <div class="cc-mwl-section-body">
                        <div class="cc-mwl-grid">${cards}</div>
                    </div>
                </section>
            `;
        }

        renderCard(item, isInStock) {
            const data = this.products.get(item.handle) || {};
            const priceCents = data.priceCents != null ? data.priceCents : parsePriceToCents(item.price);
            const vendor = data.vendor || '';
            const lowQty = data.lowStockQty;
            const added = relativeAdded(item.added_at);
            const img = this.optimizedImageSet(item.image);

            // Stock badge: low > in > out
            let badge = '';
            if (isInStock && lowQty) {
                const txt = lowQty === 1 ? t('badge_last', 'Laatste!') : t('badge_low', 'Nog {n} op voorraad', { n: lowQty });
                badge = `<span class="cc-mwl-card-badge cc-mwl-card-badge--low"><span class="cc-mwl-card-badge-dot"></span>${escapeHtml(txt)}</span>`;
            } else if (isInStock) {
                badge = `<span class="cc-mwl-card-badge cc-mwl-card-badge--in"><span class="cc-mwl-card-badge-dot"></span>${escapeHtml(t('in_stock', 'Op voorraad'))}</span>`;
            } else {
                badge = `<span class="cc-mwl-card-badge cc-mwl-card-badge--out"><span class="cc-mwl-card-badge-dot"></span>${escapeHtml(t('sold_out', 'Uitverkocht'))}</span>`;
            }

            const action = isInStock
                ? `<button type="button" class="cc-mwl-card-btn cc-mwl-card-btn--primary" data-action="add-to-cart">
                       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                         <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                         <path d="M1 1h4l2.7 13.4a2 2 0 0 0 2 1.6h9.7a2 2 0 0 0 2-1.6L23 6H6"/>
                       </svg>
                       <span class="cc-mwl-card-btn-label">${escapeHtml(t('add_to_cart', 'In winkelwagen'))}</span>
                   </button>`
                : `<a class="cc-mwl-card-btn cc-mwl-card-btn--secondary" href="${productUrl(item.handle)}">${escapeHtml(t('view_product', 'Bekijk product'))}</a>`;

            return `
                <article class="cc-mwl-card ${isInStock ? '' : 'is-out'}" data-product-id="${escapeHtml(item.id)}" data-product-handle="${escapeHtml(item.handle)}">
                    <div class="cc-mwl-card-media">
                        <a class="cc-mwl-card-img" href="${productUrl(item.handle)}" aria-label="${escapeHtml(this.titleOf(item) || 'Product')}">
                            <img src="${escapeHtml(img.src)}"
                                 ${img.srcset ? `srcset="${escapeHtml(img.srcset)}"` : ''}
                                 sizes="(max-width: 575px) 50vw, (max-width: 767px) 33vw, (max-width: 1199px) 25vw, 280px"
                                 alt="${escapeHtml(item.title || '')}" loading="lazy"
                                 onerror="this.onerror=null;this.removeAttribute('srcset');this.src='data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 63 88%22><rect width=%2263%22 height=%2288%22 fill=%22%23f3f4f6%22/></svg>'">
                        </a>
                        ${badge}
                        <button type="button" class="cc-mwl-card-remove" data-action="remove" aria-label="${escapeHtml(t('remove', 'Verwijder uit wishlist'))}">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                <path d="M10 11v6M14 11v6"/>
                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                            </svg>
                        </button>
                    </div>
                    <div class="cc-mwl-card-body">
                        ${vendor ? `<p class="cc-mwl-card-vendor">${escapeHtml(vendor)}</p>` : ''}
                        <a class="cc-mwl-card-title" href="${productUrl(item.handle)}">${escapeHtml(this.titleOf(item))}</a>
                        <div class="cc-mwl-card-price-row">
                            <span class="cc-mwl-card-price">${formatMoney(priceCents)}</span>
                            ${added ? `<span class="cc-mwl-card-added">${escapeHtml(added)}</span>` : ''}
                        </div>
                        <div class="cc-mwl-card-actions">${action}</div>
                    </div>
                </article>
            `;
        }

        // Returns { src, srcset } voor het image-element. We strippen Shopify's
        // size-suffix uit het pad (bv. "_100x150_crop_center.jpg") want anders
        // negeert de CDN onze ?width-param en blijft de lage-res image staan.
        // Voor non-Shopify URLs alleen src zonder srcset.
        optimizedImageSet(src) {
            if (!src || typeof src !== 'string') return { src: '', srcset: '' };
            let u = src;
            if (u.startsWith('//')) u = 'https:' + u;
            if (!u.includes('cdn.shopify.com') && !u.includes('shopify.com/s/files')) {
                return { src: u, srcset: '' };
            }
            let base = u.split('?')[0];
            // Strip size-suffix: _pico/_icon/_thumb/_small/_compact/_medium/_large/
            // _grande/_original/_master, of _NxN, _Nx, _xN, eventueel met
            // _crop_<position> suffix. Behoud bestand-extensie.
            base = base.replace(
                /_(pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d*|x\d+|\d+x)(_crop_(?:center|top|bottom|left|right|top_left|top_right|bottom_left|bottom_right))?(\.(?:jpe?g|png|gif|webp|avif))/i,
                '$3'
            );
            const widths = IMAGE_WIDTHS;
            const srcset = widths.map(w => `${base}?width=${w}&quality=90 ${w}w`).join(', ');
            return { src: `${base}?width=600&quality=90`, srcset };
        }

        toggleEmptyState(show) {
            if (!this.emptyEl || !this.sectionsEl) return;
            if (this.items.length === 0) {
                // True empty wishlist = onboarding state
                this.renderEmpty(t('empty_title', 'Je wishlist is leeg'), t('empty_text', 'Klik op het hartje bij producten om ze hier te verzamelen. Zo zie je in één oogopslag wanneer een kaart of product weer op voorraad is.'), true);
                this.sectionsEl.style.display = 'none';
                if (this.summaryEl) this.summaryEl.style.display = 'none';
                if (this.controlsEl) this.controlsEl.style.display = 'none';
                if (this.chipsEl) this.chipsEl.style.display = 'none';
                return;
            }
            if (show) {
                // Filter levert geen resultaten
                this.renderEmpty(t('no_results_title', 'Geen resultaten'), t('no_results_text', 'Geen items die voldoen aan je filter of zoekterm. Pas de filters aan of wis ze.'), false);
                this.sectionsEl.style.display = 'none';
            } else {
                this.emptyEl.style.display = 'none';
                this.sectionsEl.style.display = '';
            }
            if (this.summaryEl) this.summaryEl.style.display = '';
            if (this.controlsEl) this.controlsEl.style.display = '';
        }

        renderEmpty(title, body, onboarding) {
            if (!this.emptyEl) return;
            this.emptyEl.style.display = 'block';
            this.emptyEl.innerHTML = `
                <div class="cc-mwl-empty-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                </div>
                <h2 class="cc-mwl-empty-title">${escapeHtml(title)}</h2>
                <p class="cc-mwl-empty-text">${escapeHtml(body)}</p>
                ${onboarding
                    ? `<a class="cc-mwl-empty-cta" href="${escapeHtml(ROOT)}">${escapeHtml(t('continue_shopping', 'Verder winkelen'))}</a>`
                    : `<button type="button" class="cc-mwl-empty-cta" data-action="reset-filters">${escapeHtml(t('clear_filters', 'Filters wissen'))}</button>`}
            `;
        }

        /* ------------------ Events ------------------ */

        bindEvents() {
            // Search input (debounced)
            const searchInput = this.root.querySelector('.cc-mwl-search input');
            if (searchInput) {
                const debounced = debounce(() => {
                    this.ui.search = searchInput.value;
                    this.root.querySelector('.cc-mwl-search').classList.toggle('has-value', !!searchInput.value);
                    this.render();
                }, SEARCH_DEBOUNCE_MS);
                searchInput.addEventListener('input', debounced);
            }

            // Sort dropdown
            const sortSel = this.root.querySelector('.cc-mwl-sort');
            if (sortSel) {
                sortSel.addEventListener('change', () => {
                    this.ui.sort = sortSel.value;
                    this.render();
                });
            }

            // Auto-remove toggle
            const arInput = this.root.querySelector('.cc-mwl-toggle input[type="checkbox"]');
            if (arInput) {
                arInput.checked = this.ui.autoRemove;
                arInput.addEventListener('change', () => {
                    this.ui.autoRemove = arInput.checked;
                    localStorage.setItem(AUTO_REMOVE_KEY, arInput.checked ? '1' : '0');
                });
            }

            // Refresh button
            const refreshBtn = this.root.querySelector('.cc-mwl-refresh');
            if (refreshBtn) {
                refreshBtn.addEventListener('click', () => this.handleRefresh(refreshBtn));
            }

            // Info modal triggers
            const infoBtn = this.root.querySelector('.cc-mwl-info-btn');
            if (infoBtn && this.modalEl) {
                infoBtn.addEventListener('click', () => this.modalEl.classList.add('is-open'));
                this.modalEl.addEventListener('click', (e) => {
                    if (e.target === this.modalEl ||
                        e.target.closest('.cc-mwl-modal-close') ||
                        e.target.closest('.cc-mwl-modal-ok')) {
                        this.modalEl.classList.remove('is-open');
                    }
                });
                document.addEventListener('keydown', (e) => {
                    if (e.key === 'Escape' && this.modalEl.classList.contains('is-open')) {
                        this.modalEl.classList.remove('is-open');
                    }
                });
            }

            // Search clear button
            const clearBtn = this.root.querySelector('.cc-mwl-search-clear');
            if (clearBtn && searchInput) {
                clearBtn.addEventListener('click', () => {
                    searchInput.value = '';
                    this.ui.search = '';
                    this.root.querySelector('.cc-mwl-search').classList.remove('has-value');
                    this.render();
                    searchInput.focus();
                });
            }

            // Bulk-add button (zit in de header naast info-knop)
            const bulkBtn = this.root.querySelector('.cc-mwl-bulk-add');
            if (bulkBtn) {
                bulkBtn.addEventListener('click', () => this.handleBulkAdd());
            }

            // Vendor chips (delegated)
            if (this.chipsEl) {
                this.chipsEl.addEventListener('click', (e) => {
                    const chip = e.target.closest('.cc-mwl-chip');
                    if (!chip) return;
                    this.ui.vendor = chip.dataset.vendor;
                    this.render();
                });
            }

            // Card actions (delegated)
            if (this.sectionsEl) {
                this.sectionsEl.addEventListener('click', (e) => {
                    const btn = e.target.closest('[data-action]');
                    if (!btn) return;
                    const action = btn.dataset.action;
                    const card = btn.closest('[data-product-id]');
                    if (!card) return;
                    const id = card.dataset.productId;
                    const handle = card.dataset.productHandle;
                    if (action === 'remove') {
                        e.preventDefault();
                        this.handleRemove(id, handle, card);
                    } else if (action === 'add-to-cart') {
                        this.handleAddToCart(id, handle, btn, card);
                    }
                });
            }

            // Empty state actions
            if (this.emptyEl) {
                this.emptyEl.addEventListener('click', (e) => {
                    const btn = e.target.closest('[data-action="reset-filters"]');
                    if (!btn) return;
                    this.ui.search = '';
                    this.ui.vendor = 'all';
                    const si = this.root.querySelector('.cc-mwl-search input');
                    if (si) si.value = '';
                    this.root.querySelector('.cc-mwl-search')?.classList.remove('has-value');
                    this.render();
                });
            }

            // Cross-tab sync
            window.addEventListener('storage', (e) => {
                if (e.key !== STORAGE_KEY) return;
                this.items = this.readItems();
                if (this.items.length === 0) {
                    this.toggleEmptyState(false);
                } else {
                    this.render();
                }
            });
        }

        /* ------------------ Actions ------------------ */

        async handleRefresh(btn) {
            btn.classList.add('is-loading');
            btn.disabled = true;
            await this.refreshAll(true);
            btn.classList.remove('is-loading');
            btn.disabled = false;
            showToast({
                message: t('refreshed', 'Wishlist vernieuwd met laatste voorraad'),
                showLink: false,
                variant: 'wishlist',
            });
        }

        handleRemove(id, handle, cardEl) {
            const idx = this.items.findIndex(i => i.id === id);
            if (idx === -1) return;
            const removed = this.items[idx];

            // Animate uit, daarna update state + render
            cardEl.classList.add('is-removing');
            const fullTitle = this.titleOf(removed) || 'Product';
            const titleShort = fullTitle.length > 40 ? fullTitle.slice(0, 38) + '…' : fullTitle;

            // Direct verwijderen uit data + localStorage; render past counts aan
            this.items.splice(idx, 1);
            this.writeItems();

            // Wacht op CSS-transition voor de remove uit DOM
            setTimeout(() => {
                this.render();
            }, 250);

            // Undo via shared toast
            let undone = false;
            showToast({
                message: t('removed', '"{title}" verwijderd', { title: titleShort }),
                action: {
                    label: t('undo', 'Ongedaan maken'),
                    onClick: () => {
                        if (undone) return;
                        undone = true;
                        // Insert terug op originele positie zodat sortering klopt
                        this.items.splice(Math.min(idx, this.items.length), 0, removed);
                        this.writeItems();
                        this.render();
                    },
                },
                variant: 'wishlist',
                timeout: UNDO_TIMEOUT_MS,
            });
        }

        async handleAddToCart(id, handle, btn, cardEl) {
            const data = this.products.get(handle);
            if (!data || !data.variantId) {
                showToast({ message: t('variant_missing', 'Variant niet gevonden, herlaad de pagina'), showLink: false });
                return;
            }

            // Qty uit metafield. Min en step worden zo gerespecteerd: bij min=5
            // voegen we direct 5 toe. Voor add-to-cart vanuit wishlist is "1×
            // de minimale eenheid" altijd 1× minimum_amount (geen losse step).
            const addQty = data.qtyMin || 1;

            // Cart-max check (cumulatief over varianten) tegen cart-cache.
            // Bij overschrijding hergebruiken we het cc:cart-max-reached event
            // dat cart-popup-preference.js opvangt voor de juiste toast.
            const check = this.checkCartMax(data, addQty);
            if (!check.ok) {
                this.dispatchMaxReached({
                    reason: check.reason,
                    max: check.max,
                    metaMax: check.metaMax,
                    inCart: check.inCart,
                    productId: data.productId,
                });
                return;
            }

            const labelEl = btn.querySelector('.cc-mwl-card-btn-label');
            const originalLabel = labelEl ? labelEl.textContent : '';
            btn.disabled = true;
            if (labelEl) labelEl.textContent = t('adding', 'Toevoegen…');

            try {
                const res = await fetch(`${ROOT}cart/add.js`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify({ items: [{ id: data.variantId, quantity: addQty }] }),
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    throw new Error(err.description || t('add_failed', 'Kon niet toevoegen aan winkelwagen'));
                }
                btn.classList.add('is-added');
                if (labelEl) labelEl.textContent = t('added', 'Toegevoegd');
                this.refreshCartCount();
                this.fetchCart(); // cache verversen voor volgende max-check

                const msg = addQty > 1
                    ? t('added_qty', '{n} stuks toegevoegd aan winkelwagen', { n: addQty })
                    : t('added_to_cart', 'Toegevoegd aan winkelwagen');
                showToast({ message: msg });

                if (this.ui.autoRemove) {
                    setTimeout(() => {
                        // Verwijder zonder undo-toast (we hebben net "toegevoegd")
                        const idx = this.items.findIndex(i => i.id === id);
                        if (idx > -1) {
                            this.items.splice(idx, 1);
                            this.writeItems();
                            cardEl.classList.add('is-removing');
                            setTimeout(() => this.render(), 250);
                        }
                    }, 600);
                } else {
                    setTimeout(() => {
                        btn.disabled = false;
                        btn.classList.remove('is-added');
                        if (labelEl) labelEl.textContent = originalLabel || t('add_to_cart', 'In winkelwagen');
                    }, 1800);
                }
            } catch (e) {
                btn.disabled = false;
                if (labelEl) labelEl.textContent = originalLabel || t('add_to_cart', 'In winkelwagen');
                showToast({
                    message: e.message || t('add_failed', 'Kon niet toevoegen aan winkelwagen'),
                    showLink: false,
                });
            }
        }

        async handleBulkAdd() {
            const inStockItems = this.items.filter(i => this.products.get(i.handle)?.available);
            if (inStockItems.length === 0) return;

            // Per item: qty_min als quantity, plus max-check tegen cart-cache.
            // Items die over de max gaan worden overgeslagen — we tonen achter-
            // af een toast met aantal geblokkeerd. Cart-cache moet eerst vers
            // zijn om correcte cumulatieve check te kunnen doen.
            await this.fetchCart();

            // Houd tussen-state bij: per product-id sommeren we wat we al
            // gepland hebben toe te voegen, zodat 2 items van hetzelfde
            // product (verschillende varianten) ook samen onder de max blijven.
            const plannedQtyByProduct = new Map();
            const lineItems = [];
            const blocked = [];

            for (const it of inStockItems) {
                const d = this.products.get(it.handle);
                if (!d || !d.variantId) continue;
                const addQty = d.qtyMin || 1;

                if (d.qtyMax && d.qtyMax > 0 && d.productId) {
                    const inCart = this.getProductQtyInCart(d.productId);
                    const planned = plannedQtyByProduct.get(d.productId) || 0;
                    if (inCart + planned + addQty > d.qtyMax) {
                        blocked.push(it);
                        continue;
                    }
                    plannedQtyByProduct.set(d.productId, planned + addQty);
                }
                lineItems.push({ id: d.variantId, quantity: addQty, _wlId: it.id });
            }

            if (lineItems.length === 0) {
                if (blocked.length > 0) {
                    showToast({
                        message: t('all_max', 'Alle items zitten al op het maximum in je winkelwagen'),
                        showLink: false,
                    });
                } else {
                    showToast({ message: t('no_variants', 'Geen geldige varianten gevonden'), showLink: false });
                }
                return;
            }

            const bulkBtn = this.root.querySelector('.cc-mwl-bulk-add');
            const lbl = bulkBtn?.querySelector('.cc-mwl-bulk-add-label');
            const original = lbl ? lbl.textContent : '';
            if (bulkBtn) bulkBtn.disabled = true;
            if (lbl) lbl.textContent = t('bulk_adding', 'Bezig met toevoegen…');

            try {
                const res = await fetch(`${ROOT}cart/add.js`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify({
                        items: lineItems.map(({ id, quantity }) => ({ id, quantity })),
                    }),
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.description || t('add_failed', 'Kon niet toevoegen aan winkelwagen'));

                this.refreshCartCount();
                this.fetchCart();

                const addedCount = Array.isArray(data.items) ? data.items.length : lineItems.length;
                let msg = addedCount === 1
                    ? t('bulk_added_one', '1 product toegevoegd aan winkelwagen')
                    : t('bulk_added_other', '{n} producten toegevoegd aan winkelwagen', { n: addedCount });
                if (blocked.length > 0) {
                    msg += ' ' + t('bulk_skipped', '({n} overgeslagen, al op maximum)', { n: blocked.length });
                }
                showToast({ message: msg });

                if (this.ui.autoRemove) {
                    const wlIds = new Set(lineItems.map(li => li._wlId));
                    this.items = this.items.filter(it => !wlIds.has(it.id));
                    this.writeItems();
                    this.render();
                }
            } catch (e) {
                showToast({
                    message: e.message || t('bulk_failed', 'Toevoegen aan winkelwagen mislukt'),
                    showLink: false,
                });
            } finally {
                if (bulkBtn) bulkBtn.disabled = false;
                if (lbl) lbl.textContent = original;
                // Re-render summary (counts/progress) — bulk-knop staat nu in header
                this.renderSummary(
                    this.items.filter(i => this.products.get(i.handle)?.available),
                    this.items.filter(i => !this.products.get(i.handle)?.available)
                );
            }
        }

        refreshCartCount() {
            // Theme heeft meerdere selectors voor cart counter; update ze allemaal.
            fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
                .then(r => r.ok ? r.json() : null)
                .then(cart => {
                    if (!cart) return;
                    const selectors = [
                        '.cart-count', '#cart-count', '.js-cart-count',
                        '.bls-header-cart .h-count', '.cart-icon-bubble .h-count',
                        '[data-cart-count]'
                    ];
                    selectors.forEach((sel) => {
                        document.querySelectorAll(sel).forEach((el) => {
                            el.textContent = String(cart.item_count);
                        });
                    });
                })
                .catch(() => { /* ignore */ });
        }
    }

    /* -------------------------------------------------------- */
    /* Boot                                                     */
    /* -------------------------------------------------------- */

    function boot() {
        const root = document.getElementById('cc-mwl-page');
        if (!root) return;
        new WishlistPage(root);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
})();
